import { CoreFetch, DocumentApi } from '../../api';
import { getDocumentAiAttachState, isAiFileContentSupported } from './ai-file-support';

export type AiDocumentPayload = {
    name: string;
    type: string;
    size: number;
    content: Uint8Array;
};

export type DocumentApiSide = 'Support' | 'Provider';

const fetchUrlAsBytes = async (
    url: string,
): Promise<{ content: Uint8Array; size: number; type: string }> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to download document (${response.status})`);
    }
    const blob = await response.blob();
    const buffer = await blob.arrayBuffer();
    return {
        content: new Uint8Array(buffer),
        size: blob.size,
        type: blob.type || 'application/octet-stream',
    };
};

export type DocumentLike = {
    id?: string;
    filename?: string;
    mimetype?: string;
    filesize?: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

/**
 * Resolve a document / letter / fax attach payload to the underlying Document API id + metadata.
 * Letters and faxes use `documentId` (and optional nested `document`).
 */
export const resolveAiFileSource = (value: unknown): DocumentLike | null => {
    if (!isRecord(value)) {
        return null;
    }

    const nested = isRecord(value.document) ? value.document : null;
    const nestedMeta = {
        filename: typeof nested?.filename === 'string' ? nested.filename : undefined,
        mimetype: typeof nested?.mimetype === 'string' ? nested.mimetype : undefined,
        filesize: typeof nested?.filesize === 'number' ? nested.filesize : undefined,
    };
    const ownMeta = {
        filename: typeof value.filename === 'string' ? value.filename : undefined,
        mimetype: typeof value.mimetype === 'string' ? value.mimetype : undefined,
        filesize: typeof value.filesize === 'number' ? value.filesize : undefined,
    };

    // Letter / fax: must have a linked documentId.
    if ('documentId' in value) {
        if (typeof value.documentId !== 'string' || !value.documentId) {
            return null;
        }
        const nestedId = typeof nested?.id === 'string' && nested.id ? nested.id : undefined;
        return {
            id: nestedId || value.documentId,
            filename: nestedMeta.filename || ownMeta.filename,
            mimetype: nestedMeta.mimetype || ownMeta.mimetype,
            filesize: nestedMeta.filesize ?? ownMeta.filesize,
        };
    }

    // Plain document row.
    if (typeof value.id === 'string' && value.id) {
        return {
            id: value.id,
            filename: ownMeta.filename,
            mimetype: ownMeta.mimetype,
            filesize: ownMeta.filesize,
        };
    }

    return null;
};

export const isPlainDocument = (value: unknown): value is DocumentLike =>
    resolveAiFileSource(value) !== null;

/**
 * Resolve a Support/Provider document to an AI context.file payload (Uint8Array content).
 * Prefer real MIME from the document record; fall back to response Content-Type / filename.
 */
export const loadDocumentAsAiFile = async (
    document: DocumentLike | unknown,
    side: DocumentApiSide,
): Promise<AiDocumentPayload> => {
    const source = resolveAiFileSource(document);
    if (!source?.id) {
        throw new Error('Document id is required');
    }

    const attachState = getDocumentAiAttachState({
        mimetype: source.mimetype,
        filename: source.filename,
        filesize: source.filesize,
        hasFile: true,
    });
    if (attachState.disabled) {
        throw new Error(attachState.disabledReason || 'This file type is not supported for AI.');
    }

    const api = CoreFetch<DocumentApi>(DocumentApi);
    const documentUrl =
        side === 'Provider'
            ? await api.getProviderDocumentUrl({ id: source.id })
            : await api.getSupportDocumentUrl({ id: source.id });

    if (!documentUrl?.url) {
        throw new Error('Document URL is not available');
    }

    const { content, size, type } = await fetchUrlAsBytes(documentUrl.url);
    const resolvedType = source.mimetype || type;
    const resolvedName = source.filename || `document-${source.id}`;

    if (!isAiFileContentSupported(resolvedType, resolvedName)) {
        throw new Error('This file type is not supported for AI.');
    }

    return {
        name: resolvedName,
        // Prefer stored MIME — extension routes PDF/Office/text/image on this field.
        type: resolvedType,
        size: typeof source.filesize === 'number' ? source.filesize : size,
        content,
    };
};

export const resolveDocumentApiSide = (utilityKey?: string): DocumentApiSide => {
    const side = utilityKey?.split(':')[1];
    return side === 'Provider' ? 'Provider' : 'Support';
};
